import { useCallback, useMemo, useState } from 'react';
import { generateTickValues } from './conjunctionCalendarComponentUtils';
import { handleGoogleAnalytics } from '@/utils';
import { LOGGING_TYPE } from '@/constants/calendar';

const useGetTimeValues = currentDate => {
  if (currentDate) {
    const startOfDay = new Date(currentDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(currentDate);
    endOfDay.setHours(23, 59, 59, 999);

    const tickValues = generateTickValues(startOfDay, 4, 24);
    const dayValues = [startOfDay];

    return { startOfDay, endOfDay, tickValues, dayValues };
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const after3Days = new Date(today);
    after3Days.setDate(today.getDate() + 4);

    const tickValues = generateTickValues(today, 4, 96);

    const dayValues = [];
    for (let i = 0; i < 4; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dayValues.push(date);
    }

    return {
      startOfDay: today,
      endOfDay: after3Days,
      tickValues,
      dayValues,
    };
  }
};

const useDateNavigation = tableData => {
  const [currentDateIndex, setCurrentDateIndex] = useState(0);

  const uniqueDates = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    const dates = tableData.map(item => {
      const date = new Date(item.tcaTime.split('\n').join('T'));
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    });

    const uniqueTimestamps = Array.from(
      new Set(dates.map(date => date.getTime()))
    );
    return uniqueTimestamps
      .map((timestamp: number) => new Date(timestamp))
      .sort((a, b) => a.getTime() - b.getTime());
  }, [tableData]);

  const filteredData = useMemo(() => {
    if (!tableData || uniqueDates.length === 0) return [];

    const currentDate = uniqueDates[currentDateIndex];
    const nextDate = new Date(currentDate);
    nextDate.setDate(currentDate.getDate() + 1);

    return tableData.filter(item => {
      const itemDate = new Date(item.tcaTime.split('\n').join('T'));
      return itemDate >= currentDate && itemDate < nextDate;
    });
  }, [tableData, uniqueDates, currentDateIndex]);

  const goToPreviousDate = useCallback(() => {
    setCurrentDateIndex(prev => Math.max(0, prev - 1));
  }, []);

  const goToNextDate = useCallback(() => {
    setCurrentDateIndex(prev => Math.min(uniqueDates.length - 1, prev + 1));
  }, [uniqueDates.length]);

  const currentDate = uniqueDates[currentDateIndex] || new Date();
  const hasPrevious = currentDateIndex > 0;
  const hasNext = currentDateIndex < uniqueDates.length - 1;

  if (!tableData) {
    return {
      currentDate: new Date(),
      filteredData: [],
      goToPreviousDate: () => {},
      goToNextDate: () => {},
      hasPrevious: false,
      hasNext: false,
      totalDates: 0,
      currentDateIndex: 0,
    };
  }

  return {
    currentDate,
    filteredData,
    goToPreviousDate,
    goToNextDate,
    hasPrevious,
    hasNext,
    totalDates: uniqueDates.length,
    currentDateIndex,
  };
};

const useChartInteraction = () => {
  const [nodeId, setNodeId] = useState(null);
  const handleMouseMove = useCallback(node => setNodeId(node.id), [setNodeId]);
  const handleMouseLeave = useCallback(() => setNodeId(null), [setNodeId]);
  const handleNodeClick = node => {
    handleMouseMove(node);
    handleGoogleAnalytics({
      eventType: LOGGING_TYPE.conjunctionClicked,
      eventLabel: node.data.CDM_ID,
    });
    const url = `https://platform.spacemap42.com/?service=conjunction&board=false&cdm_id=${node.data.CDM_ID}`;
    window.open(url, '_blank');
  };
  const getNodeSize = useMemo(
    () => node => {
      if (nodeId !== null && nodeId === node.id) return 30;
      return 10;
    },
    [nodeId]
  );

  return {
    nodeId,
    handleMouseMove,
    handleMouseLeave,
    handleNodeClick,
    getNodeSize,
  };
};

export { useGetTimeValues, useChartInteraction, useDateNavigation };
